import { deleteAlertList } from '@/api/home';
import { DeleteOutlined, ExclamationCircleOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons';
import { Avatar, List, message, Tag, Typography } from 'antd';
import React, { useEffect, useState } from 'react';
import './SystemMessagePanel.less';

const { Title, Text } = Typography;

interface AlertItem {
  alert_list_id: string;
  alert_name: string;
  device_name: string;
  trigger_time: number;
  trigger_format_time_ago: string;
  alert_level: string;
}

interface SystemMessagePanelProps {
  alertList?: AlertItem[];
  onDeleteSuccess?: () => void;
  onCollapseChange?: (collapsed: boolean) => void;
}

// 与 AlertPlate 组件保持一致的颜色配置
const levelColorMap: Record<string, string> = {
  紧急: '#FF8C42', // 橙色
  重要: '#4A90E2', // 蓝色
  次要: '#87CEEB', // 浅蓝色
  提示: '#9370DB', // 紫色
};

const SystemMessagePanel: React.FC<SystemMessagePanelProps> = ({ alertList = [], onDeleteSuccess, onCollapseChange }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [displayList, setDisplayList] = useState<AlertItem[]>(alertList || []);
  const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    setDisplayList(alertList || []);
  }, [alertList]);

  const handleDelete = async (id: string) => {
    if (deletingIds.has(id)) return; // 防止重复点击

    setDeletingIds((prev) => new Set(prev).add(id));
    
    try {
      const resp = await deleteAlertList(id);
      if (resp.success) {
        message.success('删除成功');
        // 先更新本地显示
        setDisplayList((prev) => prev.filter((item) => item.alert_list_id !== id));
        // 调用回调函数刷新数据
        onDeleteSuccess?.();
      } else {
        message.error(resp.errorMsg || '删除失败');
      }
    } catch (error) {
      message.error('删除失败');
      console.error('删除告警消息失败:', error);
    } finally {
      setDeletingIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });
    }
  };

  return (
    <>
      <div className={`system-message-panel ${collapsed ? 'collapsed' : ''}`}>
        {!collapsed && (
          <div className="system-message-panel__content">
            <Title level={5} className="system-message-panel__title">
              告警消息
            </Title>
            <List
              dataSource={displayList}
              renderItem={(item) => (
                <List.Item className="system-message-panel__item">
                  <List.Item.Meta
                    avatar={<Avatar icon={<ExclamationCircleOutlined />} size="small" style={{ backgroundColor: levelColorMap[item.alert_level] || '#999' }} />}
                    title={
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '2px', whiteSpace: 'nowrap' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flex: 1, minWidth: 0 }}>
                          <Text 
                            style={{ 
                              fontSize: '12px',
                              whiteSpace: 'nowrap',
                            }}
                            title={item.alert_name}
                          >
                            {item.alert_name.length > 8 ? `${item.alert_name.substring(0, 8)}...` : item.alert_name}
                          </Text>
                          <Tag color={levelColorMap[item.alert_level]} style={{ fontSize: '10px', padding: '0 4px', lineHeight: '16px', flexShrink: 0, marginRight: '2px' }}>
                            {item.alert_level}
                          </Tag>
                        </div>
                        <DeleteOutlined
                          style={{
                            fontSize: '14px',
                            color: deletingIds.has(item.alert_list_id) ? '#ccc' : '#999',
                            cursor: deletingIds.has(item.alert_list_id) ? 'not-allowed' : 'pointer',
                            flexShrink: 0,
                            marginLeft: '4px',
                          }}
                          onClick={() => handleDelete(item.alert_list_id)}
                          onMouseEnter={(e) => {
                            if (!deletingIds.has(item.alert_list_id)) {
                              e.currentTarget.style.color = '#ff4d4f';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!deletingIds.has(item.alert_list_id)) {
                              e.currentTarget.style.color = '#999';
                            }
                          }}
                        />
                      </div>
                    }
                    description={
                      <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        <Text type="secondary" style={{ fontSize: '11px' }}>
                          {item.device_name} · {item.trigger_format_time_ago}
                        </Text>
                      </div>
                    }
                  />
                </List.Item>
              )}
              pagination={false}
              locale={{ emptyText: '暂无告警消息' }}
            />
          </div>
        )}
      </div>
      <div className={`system-message-panel__toggle ${collapsed ? 'collapsed' : ''}`} onClick={() => { const next = !collapsed; setCollapsed(next); onCollapseChange?.(next); }}>
        {collapsed ? <LeftOutlined /> : <RightOutlined />}
      </div>
    </>
  );
};

export default SystemMessagePanel;
